import React, { useEffect, useState } from "react";
import { Component, Shape, UnifiedElement } from "@/types/diagram";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import useGetCategoriesService from "@/service-hooks/use-get-cagetories-service";
import useUpdateShapeService from "@/service-hooks/use-update-shape-service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { X } from "lucide-react";
import { processSVGFile } from "@/lib/svg-utils.lib";

interface EditElementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  element: UnifiedElement;
}

const EditElementDialog: React.FC<EditElementDialogProps> = ({
  isOpen,
  onClose,
  element,
}) => {
  const [elementData, setElementData] = useState(element);
  const [category, setCategory] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>(element.tags || []);
  const [newTag, setNewTag] = useState("");
  const [userHasSelectedCategory, setUserHasSelectedCategory] = useState(false);
  const queryClient = useQueryClient();
  useEffect(() => {
    setElementData(element);
    setTags(element.tags || []);
    setCategory("");
    setUserHasSelectedCategory(false);
  }, [element.name]); // Match the dependency from previous version

  const { data: categories } = useGetCategoriesService(true);

  useEffect(() => {
    // Only auto-set category if user hasn't manually selected one
    if (categories?.data && !userHasSelectedCategory) {
      categories.data.forEach((categoryItem) => {
        if (categoryItem.path === elementData.path) {
          setCategory(categoryItem._id);
        }
      });
    }
  }, [elementData, categories, userHasSelectedCategory]);

  const { mutate: updateShapeMutation, isPending: isUpdatePending } =
    useUpdateShapeService((_, error) => {
      if (error) return;
      queryClient.invalidateQueries({ queryKey: ["shapes-by-category"] });
      queryClient.invalidateQueries({ queryKey: ["searched-shapes"] });
    });

  const handleTagAdd = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && newTag.trim()) {
      e.preventDefault();
      if (!tags.includes(newTag.trim())) {
        const updatedTags = [...tags, newTag.trim()];
        setTags(updatedTags);
        setElementData((prev) => ({ ...prev, tags: updatedTags }));
      }
      setNewTag("");
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(updatedTags);
    setElementData((prev) => ({ ...prev, tags: updatedTags }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!category) {
      setError("Category is required");
      return;
    }

    const updatePayload = {
      _id: elementData._id,
      name: elementData.name,
      description: elementData.description,
      version: elementData.version || "1.0.0",
      tags: elementData.tags || [],
      status: elementData.status || "active",
      category: category,
      svgContent: elementData.svgContent,
    };

    updateShapeMutation(updatePayload, {
      onSuccess: () => {
        // Update the query cache similar to the previous version
        queryClient.setQueryData(
          ["shapes_data"],
          (oldData: {
            shapes: Shape[];
            components: Component[];
            total: number;
          }) => {
            if (!oldData) return;
            const updatedShapes = oldData.shapes.map((shape) =>
              shape.name === elementData.name
                ? ({
                    ...elementData,
                    path: categories?.data.find((el) => category === el._id)
                      ?.path,
                  } as Shape)
                : shape
            );

            const updatedComponents = oldData.components.map((component) =>
              component.name === elementData.name
                ? ({
                    ...elementData,
                    path: categories?.data.find((el) => category === el._id)
                      ?.path,
                  } as Component)
                : component
            );

            return {
              shapes: updatedShapes,
              components: updatedComponents,
              total: oldData.total,
            };
          }
        );
        onClose();
      },
      onError: (error) => {
        console.error("Failed to update element:", error);
        setError("Failed to update element");
      },
    });
  };
  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setUserHasSelectedCategory(true);
    setError(null);
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[800px] max-h-[80vh] bg-surface-elevated text-white overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit: {elementData.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Edit the fields for this element.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Name
            </label>
            <Input
              value={elementData.name || ""}
              onChange={(e) =>
                setElementData((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              className="w-full bg-surface-active text-white placeholder:text-muted"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Version
            </label>
            <Input
              value={elementData.version || ""}
              onChange={(e) =>
                setElementData((prev) => ({
                  ...prev,
                  version: e.target.value,
                }))
              }
              className="w-full bg-surface-active text-white placeholder:text-muted"
              placeholder="1.0.0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Description
            </label>
            <Textarea
              value={elementData.description || ""}
              onChange={(e) =>
                setElementData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter description"
              className="w-full bg-surface-input text-white border-surface-border"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Category
            </label>

            <Select
              key={category}
              value={category || ""}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="w-full bg-surface-input text-white border-surface-border">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-surface-elevated border-surface-border">
                {categories?.data?.map((cat) => (
                  <SelectItem
                    key={cat._id}
                    value={cat._id}
                    className="text-white hover:bg-surface-hover"
                  >
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <div className="text-error text-sm mt-1">{error}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Status
            </label>
            <Select
              value={elementData.status || "active"}
              onValueChange={(value) =>
                setElementData((prev) => ({
                  ...prev,
                  status: value as "active" | "inactive",
                }))
              }
            >
              <SelectTrigger className="w-full bg-surface-input text-white border-surface-border">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="bg-surface-elevated border-surface-border">
                <SelectItem
                  value="active"
                  className="text-white hover:bg-surface-hover"
                >
                  Active
                </SelectItem>
                <SelectItem
                  value="inactive"
                  className="text-white hover:bg-surface-hover"
                >
                  Inactive
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="bg-surface-active text-white flex items-center gap-1"
                >
                  {tag}
                  <X
                    size={12}
                    className="cursor-pointer hover:text-error"
                    onClick={() => handleTagRemove(tag)}
                  />
                </Badge>
              ))}
            </div>
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleTagAdd}
              placeholder="Add tags and press Enter"
              className="w-full bg-surface-active text-white placeholder:text-muted"
            />
          </div>

          {/* SVG Upload for non-component elements */}
          {elementData.type && elementData.type !== "COMPONENT" && (
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Upload SVG
              </label>
              <Input
                type="file"
                accept=".svg"
                onChange={async (e) => {
                  if (e.target.files?.[0]) {
                    try {
                      const svg = await processSVGFile(e.target.files[0]);
                      setElementData((prev) => ({
                        ...prev,
                        svgContent: svg || "",
                      }));
                    } catch (error) {
                      console.error("Error processing SVG file:", error);
                      setError("Failed to process SVG file");
                    }
                  }
                }}
                className="w-full bg-surface-active text-white placeholder:text-muted"
              />
              {elementData.svgContent && (
                <div className="text-xs text-muted mt-1">
                  SVG content loaded ({elementData.svgContent.length}{" "}
                  characters)
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUpdatePending}
              className="bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
            >
              {isUpdatePending ? "Updating..." : "Update"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditElementDialog;
